/**
 * Enhanced Progress Indicator
 * Multi-step progress indicator for long-running operations
 */

import { CheckCircle, Circle } from "@phosphor-icons/react";
import { cn } from "@stylingcv/utils";
import React from "react";

type Step = {
  id: string;
  title: string;
  description?: string;
  status: "pending" | "in-progress" | "completed" | "error";
  progress?: number;
};

type EnhancedProgressIndicatorProps = {
  steps: Step[];
  currentStep?: string;
  className?: string;
};

export const EnhancedProgressIndicator: React.FC<EnhancedProgressIndicatorProps> = ({
  steps,
  currentStep,
  className,
}) => {
  const getStepIcon = (step: Step) => {
    switch (step.status) {
      case "completed": {
        return <CheckCircle className="size-5 text-green-600" weight="fill" />;
      }
      case "in-progress": {
        return (
          <div className="relative">
            <Circle className="size-5 text-blue-600" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="size-2 animate-pulse rounded-full bg-blue-600" />
            </div>
          </div>
        );
      }
      case "error": {
        return <Circle className="size-5 text-red-600" weight="fill" />;
      }
      default: {
        return <Circle className="size-5 text-gray-400" />;
      }
    }
  };

  const getStepColor = (step: Step) => {
    switch (step.status) {
      case "completed": {
        return "text-green-600";
      }
      case "in-progress": {
        return "text-blue-600";
      }
      case "error": {
        return "text-red-600";
      }
      default: {
        return "text-gray-500";
      }
    }
  };

  return (
    <div className={cn("space-y-4", className)}>
      {steps.map((step, index) => (
        <div key={step.id} className="flex items-start space-x-3">
          {/* Step Icon */}
          <div className="mt-0.5 shrink-0">{getStepIcon(step)}</div>

          {/* Step Content */}
          <div className="min-w-0 flex-1">
            <div className={cn("font-medium", getStepColor(step))}>{step.title}</div>

            {step.description && (
              <div className="mt-1 text-sm text-muted-foreground">{step.description}</div>
            )}

            {/* Progress Bar for In-Progress Steps */}
            {step.status === "in-progress" && typeof step.progress === "number" && (
              <div className="mt-2">
                <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                  <div
                    className="h-2 rounded-full bg-blue-600 transition-all duration-300 ease-out"
                    style={{ width: `${Math.min(100, Math.max(0, step.progress))}%` }}
                  />
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {Math.round(step.progress)}% complete
                </div>
              </div>
            )}
          </div>

          {/* Connector Line */}
          {index < steps.length - 1 && (
            <div className="absolute left-[10px] mt-8 h-6 w-px bg-gray-300 dark:bg-gray-600" />
          )}
        </div>
      ))}
    </div>
  );
};

// Simple loading spinner component
export const LoadingSpinner: React.FC<{ size?: "sm" | "md" | "lg"; className?: string }> = ({
  size = "md",
  className,
}) => {
  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-6 w-6",
    lg: "h-8 w-8",
  };

  return (
    <div className={cn("flex items-center justify-center", className)}>
      <div
        className={cn(
          "animate-spin rounded-full border-2 border-gray-300 border-t-blue-600",
          sizeClasses[size],
        )}
      />
    </div>
  );
};

// Loading overlay component
export const LoadingOverlay: React.FC<{
  isLoading: boolean;
  message?: string;
  children: React.ReactNode;
}> = ({ isLoading, message, children }) => {
  return (
    <div className="relative">
      {children}

      {isLoading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="flex flex-col items-center space-y-3 rounded-lg bg-card p-6 shadow-lg">
            <LoadingSpinner size="lg" />
            {message && <p className="text-sm text-muted-foreground">{message}</p>}
          </div>
        </div>
      )}
    </div>
  );
};
